import { IDENTITY_PATHS, type IdentityPath, identityAuthUrl, joinGamePath } from '@words-happen/app-urls';
import { JoinGameScreen, type JoinGameScreenState } from '@words-happen/screens';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createGamesApi, type GamesApi, lobbyFailure } from '../../api/games';
import { type BrowserLocation, windowLocation } from '../../navigation';
import { ROUTES } from '../../routes';
import { useSession } from '../../session';
import { useLobbyFailure } from './use-lobby-failure';

export interface JoinGamePageProps {
  /** Replaces words-happen-gql's lobby API. */
  gamesApi?: GamesApi;
  /** The identity app's origin. Defaults to this build's environment. */
  identityOrigin?: string;
  /** How Create free account and Sign in leave the app. Defaults to the window's location. */
  browserLocation?: BrowserLocation;
}

/**
 * The invite landing page, /join/:code (WH-155), where an invite link opens. Signed in, it joins at once (joinGame),
 * which is safe to repeat: opening the link again gives the same game. A guest is asked to create an account or sign
 * in, with this page as return_to, so they come back and join. A player with no display name is sent to choose one
 * and brought back here, and joins.
 */
export const JoinGamePage: React.FC<JoinGamePageProps> = ({
  gamesApi,
  identityOrigin = IDENTITY_URL,
  browserLocation = windowLocation,
}) => {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const { signedIn } = useSession();
  const handleFailure = useLobbyFailure();
  const [api] = useState(() => gamesApi ?? createGamesApi());
  const [state, setState] = useState<JoinGameScreenState>({ kind: 'joining' });
  const joining = useRef(false);

  const join = useCallback(async () => {
    if (joining.current) return;
    joining.current = true;
    setState({ kind: 'joining' });
    try {
      const game = await api.joinGame(code);
      setState({ kind: 'joined', opponentName: game.opponentName ?? '' });
    } catch (error) {
      const message = handleFailure(error, joinGamePath(code));
      if (message !== undefined) {
        const retry = lobbyFailure(error).kind === 'failed' ? () => void join() : undefined;
        setState({ kind: 'refused', message, onRetry: retry });
      }
    } finally {
      joining.current = false;
    }
  }, [api, code, handleFailure]);

  useEffect(() => {
    if (signedIn) void join();
  }, [signedIn, join]);

  const goToIdentity = (path: IdentityPath) =>
    browserLocation.assign(identityAuthUrl(identityOrigin, path, { returnTo: browserLocation.href() }));

  return (
    <JoinGameScreen
      state={
        signedIn
          ? state
          : {
              kind: 'guest',
              onCreateAccount: () => goToIdentity(IDENTITY_PATHS.register),
              onSignIn: () => goToIdentity(IDENTITY_PATHS.signIn),
            }
      }
      onShowGames={() => navigate(ROUTES.games)}
      onBack={() => navigate(ROUTES.dashboard)}
    />
  );
};
